import React, { useState } from 'react';
import { useDeals } from '../context/DealsContext';
import { FiX, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const DealModal = ({ deal, onClose }) => {
  const { addDeal, updateDeal } = useDeals();
  const isEditing = !!deal;

  const [formData, setFormData] = useState({
    company: deal?.company || '',
    contact: deal?.contact || '',
    value: deal?.value || '',
    stage: deal?.stage || 'Discovery',
    probability: deal?.probability || 0,
    qualityScore: deal?.qualityScore || 5,
    notes: deal?.notes || '',
    // Updated fields with separated name and contact info
    purchaserName: deal?.purchaserName || '',
    purchaserContact: deal?.purchaserContact || '',
    purchaserContactType: deal?.purchaserContactType || 'email',
    purchaserName2: deal?.purchaserName2 || '',
    purchaserContact2: deal?.purchaserContact2 || '',
    purchaserContactType2: deal?.purchaserContactType2 || 'email',
    projectedCloseDate: deal?.projectedCloseDate || '',
  });

  const stages = [
    'Discovery',
    'Qualification',
    'Demo/Proposal',
    'Negotiation',
    'Closed Won',
    'Closed Lost'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const dealData = {
      ...formData,
      value: parseInt(formData.value),
      probability: parseInt(formData.probability),
      qualityScore: parseFloat(formData.qualityScore)
    };

    if (isEditing) {
      updateDeal(deal.id, dealData);
    } else {
      addDeal(dealData);
    }
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Preset quality score options
  const qualityPresets = [
    { label: "Excellent (9-10)", value: 9.5 },
    { label: "Good (7-8)", value: 7.5 },
    { label: "Average (5-6)", value: 5.5 },
    { label: "Poor (3-4)", value: 3.5 },
    { label: "Very Poor (1-2)", value: 1.5 }
  ];

  const applyQualityPreset = (value) => {
    setFormData(prev => ({
      ...prev,
      qualityScore: value
    }));
  };

  // Preset probability options based on stage
  const probabilityByStage = {
    'Discovery': 10,
    'Qualification': 30,
    'Demo/Proposal': 50,
    'Negotiation': 75,
    'Closed Won': 100,
    'Closed Lost': 0
  };

  const applyProbabilityByStage = () => {
    setFormData(prev => ({
      ...prev,
      probability: probabilityByStage[formData.stage] || 0
    }));
  };

  // Calculate a suggested close date based on stage
  const suggestCloseDate = () => {
    const today = new Date();
    const daysToAdd = {
      'Discovery': 90,
      'Qualification': 60,
      'Demo/Proposal': 30,
      'Negotiation': 14,
      'Closed Won': 0,
      'Closed Lost': 0
    };
    
    today.setDate(today.getDate() + (daysToAdd[formData.stage] || 0));
    const suggestedDate = today.toISOString().split('T')[0];
    
    setFormData(prev => ({
      ...prev,
      projectedCloseDate: suggestedDate
    }));
  };

  // Calculate quality score based on completeness
  const calculateQualityFromCompleteness = () => {
    let score = 5; // Base score
    
    // Add points for key deal strength factors
    if (formData.purchaserName && formData.purchaserContact) score += 1.5;
    if (formData.purchaserName2 && formData.purchaserContact2) score += 1.5;
    if (formData.projectedCloseDate) score += 1;
    if (formData.notes && formData.notes.length > 20) score += 1;
    
    // Cap at 10
    score = Math.min(10, score);
    
    setFormData(prev => ({
      ...prev,
      qualityScore: score
    }));
  };

  // Format the date display for projected close date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Handle the "Optional" dropdown selection for secondary purchaser
  const [showSecondaryPurchaser, setShowSecondaryPurchaser] = useState(
    !!(deal?.purchaserName2 || deal?.purchaserContact2)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Deal' : 'Add New Deal'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company Name
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Primary Contact Person
              </label>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Deal Value ($)
              </label>
              <input
                type="number"
                name="value"
                value={formData.value}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-lg border border-blue-100 dark:border-blue-800/50">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-4">Deal Strength Factors</h3>

            {/* Primary Purchaser Contact */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">
                Primary Purchaser
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name</label>
                  <input
                    type="text"
                    name="purchaserName"
                    value={formData.purchaserName}
                    onChange={handleChange}
                    placeholder="Full name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Contact ({formData.purchaserContactType})
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      name="purchaserContactType"
                      value={formData.purchaserContactType}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm w-full sm:w-24 mb-2 sm:mb-0"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                    <input
                      type={formData.purchaserContactType === 'email' ? 'email' : 'tel'}
                      name="purchaserContact"
                      value={formData.purchaserContact}
                      onChange={handleChange}
                      placeholder={formData.purchaserContactType === 'email' ? 'Email address' : 'Phone number'}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Purchaser with Optional Toggle */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-blue-800 dark:text-blue-300">
                  Secondary Purchaser (Optional)
                </label>
                <div className="flex items-center">
                  <select
                    value={showSecondaryPurchaser ? "show" : "hide"}
                    onChange={(e) => setShowSecondaryPurchaser(e.target.value === "show")}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                  >
                    <option value="hide">Hide</option>
                    <option value="show">Show</option>
                  </select>
                </div>
              </div>
              
              {showSecondaryPurchaser && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name</label>
                    <input
                      type="text"
                      name="purchaserName2"
                      value={formData.purchaserName2}
                      onChange={handleChange}
                      placeholder="Full name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Contact ({formData.purchaserContactType2})
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <select
                        name="purchaserContactType2"
                        value={formData.purchaserContactType2}
                        onChange={handleChange}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm w-full sm:w-24 mb-2 sm:mb-0"
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                      </select>
                      <input
                        type={formData.purchaserContactType2 === 'email' ? 'email' : 'tel'}
                        name="purchaserContact2"
                        value={formData.purchaserContact2}
                        onChange={handleChange}
                        placeholder={formData.purchaserContactType2 === 'email' ? 'Email address' : 'Phone number'}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}
              {!showSecondaryPurchaser && (
                <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Secondary purchaser information hidden. Select "Show" to add this information.
                </div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Projected Close Date
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="date"
                  name="projectedCloseDate"
                  value={formData.projectedCloseDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={suggestCloseDate}
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm whitespace-nowrap"
                >
                  Suggest Date Based on Stage
                </button>
              </div>
              {formData.projectedCloseDate && (
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Display format: {formatDisplayDate(formData.projectedCloseDate)}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={calculateQualityFromCompleteness}
              className="mt-6 w-full px-4 py-2 bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Calculate Quality Based on Completeness
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Stage
              </label>
              <div className="relative">
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                >
                  {stages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
              </div>
              <button
                type="button"
                onClick={applyProbabilityByStage}
                className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Set recommended probability
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Probability (%)
              </label>
              <input
                type="number"
                name="probability"
                value={formData.probability}
                onChange={handleChange}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Quality Score (1-10)
            </label>
            <input
              type="number"
              name="qualityScore"
              value={formData.qualityScore}
              onChange={handleChange}
              min="1"
              max="10"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {qualityPresets.map(preset => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => applyQualityPreset(preset.value)}
                  className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors text-gray-800 dark:text-white"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add notes about this deal..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isEditing ? 'Update Deal' : 'Add Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealModal;